import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { useAuth } from '@/contexts/AuthenticationContext';
import {
  Clinic,
  getClinicByMobileNumber,
  getClinicForAuthenticatedUser,
  updateClinic
} from '@/data/Clinics';
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';

interface ClinicContextType {
  selectedClinic: Clinic | null;
  selectClinic: (clinic: Clinic | null) => Promise<void>;
  fetchClinic: () => void;
  updateClinicMutation: UseMutationResult<
    Clinic | null,
    Error,
    { clinicId: string | null | undefined; clinic: Clinic }
  >;
  isClinic: boolean | undefined;
  loading: boolean;
  loaded: boolean;
  fetchClinicByMobileNumber: (mobileNumber: string | null) => void;
  clinicByMobileNumber: Clinic | null | undefined;
  loadingClinicByMobileNumber: boolean;
  loadedClinicByMobileNumber: boolean;
}

const ClinicContext = createContext<ClinicContextType | undefined>(undefined);

interface ClinicProviderProps {
  children: React.ReactNode;
}

export const ClinicProvider: React.FC<ClinicProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [mobileNumberQuery, setMobileNumberQuery] = useState<string | null>(
    null
  );

  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [isClinic, setIsClinic] = useState<boolean | undefined>(undefined);

  const {
    data: clinic,
    isLoading: loading,
    isFetched: loaded,
    refetch: fetchClinic,
    isRefetching: reloading
  }: UseQueryResult<Clinic | null, Error> = useQuery({
    queryKey: ['clinic', user?.id],
    queryFn: () => getClinicForAuthenticatedUser(user?.id),
    enabled: !!user?.id
  });

  const {
    data: clinicByMobileNumber,
    isLoading: loadingClinicByMobileNumber,
    isFetched: loadedClinicByMobileNumber
  }: UseQueryResult<Clinic | null, Error> = useQuery({
    queryKey: ['clinic', 'mobileNumber', mobileNumberQuery],
    queryFn: () => getClinicByMobileNumber(mobileNumberQuery),
    enabled: !!mobileNumberQuery
  });

  const fetchClinicByMobileNumber = useCallback(
    (mobileNumber: string | null) => {
      setMobileNumberQuery(mobileNumber);
    },
    []
  );

  const updateClinicMutation = useMutation({
    mutationFn: updateClinic,
    onSuccess: (updatedClinic) => {
      if (updatedClinic) {
        setSelectedClinic(updatedClinic);
        queryClient.setQueryData(['clinic', user?.id], updatedClinic);
      }
    }
  });

  useEffect(() => {
    if (clinic) {
      setSelectedClinic(clinic);
      setIsClinic(true);
    } else if (clinic === null && !loading) {
      setIsClinic(false);
      setSelectedClinic(null);
    }
  }, [clinic, loading]);

  const selectClinic = useCallback(
    async (clinic: Clinic | null) => {
      if (clinic !== null && isClinic === undefined) return;
      setSelectedClinic(clinic);

      if (clinic === null) {
        queryClient.invalidateQueries({ queryKey: ['clinic', user?.id] });
      }
    },
    [isClinic, queryClient]
  );

  useEffect(() => {
    if (!user?.id) selectClinic(null);
  }, [user?.id, selectClinic]);

  useEffect(() => {
    return () => {
      setSelectedClinic(null);
      setIsClinic(undefined);
    };
  }, []);

  return (
    <ClinicContext.Provider
      value={{
        selectedClinic,
        selectClinic,
        fetchClinic,
        updateClinicMutation,
        isClinic,
        loading: loading || reloading,
        loaded,
        fetchClinicByMobileNumber,
        clinicByMobileNumber: clinicByMobileNumber ?? null,
        loadingClinicByMobileNumber,
        loadedClinicByMobileNumber
      }}
    >
      {children}
    </ClinicContext.Provider>
  );
};

export const useClinic = () => {
  const context = useContext(ClinicContext);
  if (context === undefined) {
    throw new Error('useClinic must be used within a ClinicProvider');
  }
  return context;
};
